/**
 * Plan-tier icon.
 *
 * Renders the shared {@link AppLogo} shield tinted with the tier's accent design
 * token so a single recognisable mark can represent any plan tier (including
 * `trial`). Thin wrapper — all colours are `var(--color-*)` tokens sourced from
 * {@link PLAN_ACCENT_TOKEN}, keeping the component token-only compliant.
 *
 * Accessibility: gives the icon an accessible name by default (derived from the
 * plan's display name), since it typically stands alone without adjacent text.
 */
import type { PlanId } from '@covered-calls/shared';
import { AppLogo } from './AppLogo';
import { planDisplayName, PLAN_ACCENT_TOKEN } from '../pages/plan-catalog';

interface PlanTierIconProps {
  /** Which plan tier this icon represents. */
  planId: PlanId;
  /** Rendered width/height in pixels (square). Defaults to 28. */
  size?: number;
  /** Optional accessible name override; defaults to '<Plan> plan'. */
  title?: string;
}

export function PlanTierIcon({ planId, size = 28, title }: PlanTierIconProps) {
  return (
    <AppLogo
      size={size}
      accent={PLAN_ACCENT_TOKEN[planId]}
      title={title ?? `${planDisplayName(planId)} plan`}
    />
  );
}
